(() => {
  if (window.ImagineWatchAnimation) return;

  const players = new Set();
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const hoverCapable = window.matchMedia('(hover: hover) and (pointer: fine)');
  let supportsGroupedMotion = Boolean(window.CSS && CSS.registerProperty);
  if (supportsGroupedMotion) {
    try {
      CSS.registerProperty({
        name: '--iw-motion-angle',
        syntax: '<angle>',
        inherits: true,
        initialValue: '0deg'
      });
    } catch (_) {
      supportsGroupedMotion = false;
    }
  }

  function pause(player, hide = false) {
    player.__imagineActivationToken = null;
    player.querySelectorAll('[data-animation-group], [data-animation-layer]').forEach((target) => {
      target.__imagineAnimation?.pause();
    });
    player.classList.remove('is-loading', 'is-playing');
    player.querySelector('.iw-animation__toggle')?.setAttribute('aria-pressed', 'false');
    if (hide && player.classList.contains('iw-animation--card')) {
      player.classList.remove('is-ready');
    }
  }

  function pauseOthers(current) {
    players.forEach((player) => {
      if (!player.isConnected) {
        players.delete(player);
      } else if (player !== current) {
        pause(player, true);
      }
    });
  }

  function loadImage(element, source) {
    if (element.dataset.loaded === 'true') return Promise.resolve();
    return new Promise((resolve, reject) => {
      element.addEventListener('load', () => {
        element.dataset.loaded = 'true';
        resolve();
      }, { once: true });
      element.addEventListener('error', reject, { once: true });
      element.src = source;
    });
  }

  async function activate(player, userInitiated = false) {
    if (player.classList.contains('is-loading')) return;

    pauseOthers(player);
    const activationToken = {};
    player.__imagineActivationToken = activationToken;

    const background = player.querySelector('.iw-animation__background');
    const foreground = player.querySelector('.iw-animation__foreground');
    const movingLayers = [...player.querySelectorAll('[data-animation-layer]')];
    const motionGroups = [...player.querySelectorAll('[data-animation-group]')];
    const animationTargets = supportsGroupedMotion && motionGroups.length
      ? motionGroups
      : movingLayers;
    const staticLayers = [...player.querySelectorAll('[data-static-layer]')];
    const blurMasks = [...player.querySelectorAll('[data-backdrop-blur]')];
    const toggle = player.querySelector('.iw-animation__toggle');

    player.classList.add('is-loading');
    try {
      await Promise.all([
        loadImage(background, player.dataset.backgroundSrc),
        ...movingLayers.map((layer) => loadImage(layer, layer.dataset.layerSrc)),
        ...staticLayers.map((layer) => loadImage(layer, layer.dataset.layerSrc)),
        loadImage(foreground, player.dataset.foregroundSrc)
      ]);

      if (player.__imagineActivationToken !== activationToken) return;

      player.classList.remove('is-loading');
      player.classList.add('is-ready');

      blurMasks.forEach((mask) => {
        const source = mask.dataset.blurMaskSrc || '';
        const ratio = Math.max(0, Number(mask.dataset.backdropBlurRatio) || 0);
        if (!source || ratio <= 0) return;
        mask.style.setProperty('--iw-blur-mask', `url(${JSON.stringify(source)})`);
        mask.style.setProperty('--iw-backdrop-blur-ratio', String(ratio));
      });

      movingLayers.forEach((layer) => {
        layer.style.mixBlendMode = layer.dataset.blendMode === 'multiply'
          ? 'multiply'
          : 'normal';
      });

      animationTargets.forEach((target) => {
        if (!target.__imagineAnimation) {
          const seconds = Math.max(1, Number(target.dataset.duration) || 60);
          const start = Number(target.dataset.startDegrees) || 0;
          const keyframes = target.matches('[data-animation-group]')
            ? [
                { '--iw-motion-angle': `${start}deg` },
                { '--iw-motion-angle': `${start + 360}deg` }
              ]
            : [
                { transform: `rotate(${start}deg)` },
                { transform: `rotate(${start + 360}deg)` }
              ];
          target.__imagineAnimation = target.animate(
            keyframes,
            { duration: seconds * 1000, iterations: Infinity, easing: 'linear' }
          );
          target.__imagineAnimation.pause();
          target.__imagineAnimation.currentTime = 0;
        }
      });

      staticLayers.forEach((layer) => {
        layer.style.mixBlendMode = layer.dataset.blendMode === 'multiply'
          ? 'multiply'
          : 'normal';
      });

      if (reduceMotion.matches && !userInitiated) {
        animationTargets.forEach((target) => target.__imagineAnimation?.pause());
      } else {
        animationTargets.forEach((target) => target.__imagineAnimation?.play());
        player.classList.add('is-playing');
        toggle?.setAttribute('aria-pressed', 'true');
      }
    } catch (error) {
      if (player.__imagineActivationToken === activationToken) {
        player.classList.remove('is-loading');
      }
      console.error('IMAGINE watch animation could not be loaded.', error);
    }
  }

  function toggle(player) {
    if (player.classList.contains('is-loading')) return;
    if (!player.classList.contains('is-ready') || !player.classList.contains('is-playing')) {
      activate(player, true);
    } else {
      pause(player);
    }
  }

  function cardForPlayer(player) {
    return player.closest('product-card, .product-card') || player.parentElement;
  }

  function initialize(root = document) {
    root.querySelectorAll('[data-imagine-watch-animation]').forEach((player) => {
      // Gallery positions can change when Shopify rejects another media item.
      // Never attach the 2D animation to a 3D render, lifestyle or unrelated photo.
      const gallery = player.closest('.product-gallery__media');
      if (gallery) {
        const image = gallery.querySelector('img:not(.iw-animation__layer)');
        const alt = image?.getAttribute('alt') || '';
        const source = image?.getAttribute('src') || '';
        const isPreview = /^(?:IMAGINE managed preview|Temporary designer preview) - [DC][0-9]+(?: - v[0-9]+)?$/.test(alt)
          || /\/shopify-product-preview-source(?:[_.?]|$)/.test(source);
        if (!isPreview) {
          pause(player);
          player.style.display = 'none';
          return;
        }
        player.style.removeProperty('display');
      }
      if (player.dataset.initialized === 'true') return;
      player.dataset.initialized = 'true';
      players.add(player);

      player.querySelector('.iw-animation__toggle')?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        toggle(player);
      });

      if (player.classList.contains('iw-animation--card')) {
        cardForPlayer(player)?.addEventListener('pointerenter', () => {
          if (hoverCapable.matches && !player.classList.contains('is-playing')) {
            activate(player, true);
          }
        });
      }
    });
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const player = entry.target;
      if (entry.isIntersecting && player.dataset.autoplay === 'true') {
        activate(player);
      } else if (!entry.isIntersecting && player.dataset.autoplay === 'true') {
        pause(player);
      }
    });
  }, { threshold: 0.35 });

  function observeAutoplayPlayers() {
    players.forEach((player) => {
      if (player.dataset.autoplay === 'true' && player.dataset.observed !== 'true') {
        player.dataset.observed = 'true';
        observer.observe(player);
      }
    });
  }

  function refresh(root = document) {
    initialize(root);
    observeAutoplayPlayers();
  }

  const dynamicContentObserver = new MutationObserver((records) => {
    const hasNewPlayer = records.some((record) => [...record.addedNodes].some((node) => (
      node.nodeType === Node.ELEMENT_NODE && (
        node.matches?.('[data-imagine-watch-animation]') ||
        node.querySelector?.('[data-imagine-watch-animation]')
      )
    )));
    if (hasNewPlayer) refresh();
  });

  window.ImagineWatchAnimation = { refresh, pauseAll: () => pauseOthers(null) };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      refresh();
      dynamicContentObserver.observe(document.documentElement, { childList: true, subtree: true });
    }, { once: true });
  } else {
    refresh();
    dynamicContentObserver.observe(document.documentElement, { childList: true, subtree: true });
  }
  document.addEventListener('shopify:section:load', (event) => refresh(event.target));
})();

